import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  addAllergyFailure,
  addAllergySuccess,
  deleteAllergyFailure,
  deleteAllergySuccess,
  editAllergyFailure,
  editAllergySuccess,
  fetchAllergyFailure,
  fetchAllergyRegisterFailure,
  fetchAllergyRegisterSuccess,
  fetchAllergySuccess,
  setRegisterAssertionFailure,
  setRegisterAssertionSuccess,
} from "./actions";
import {
  ADD_ALLERGY,
  DELETE_ALLERGY,
  EDIT_ALLERGY,
  FETCH_ALLERGY,
  FETCH_ALLERGY_REGISTER,
  SET_REGISTER_ASSERTION,
} from "./constants";

export function* fetchAllergySaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  let query = payload?.data ? QueryString.stringify(payload?.data) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident-allergy?${query}`,
      payload,
    );
    yield put(fetchAllergySuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchAllergyFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching allergies!"));
  }
}

export function* addAllergySaga({ payload, onAdd, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const data = yield call(
      api(token).post,
      `/api/resident-allergy/add`,
      payload,
    );
    if (data) {
      yield put(successAlert("Allergy added successfully!"));
      yield put(addAllergySuccess());
      typeof onAdd === "function" && onAdd();
    }
  } catch (e) {
    yield put(addAllergyFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* editAllergySaga({ payload, onEdit, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const data = yield call(
      api(token).post,
      `/api/resident-allergy/edit/${payload.id}`,
      payload.data,
    );
    if (data) {
      yield put(successAlert("Allergy updated successfully!"));
      yield put(editAllergySuccess());
      typeof onEdit === "function" && onEdit(data);
    }
  } catch (e) {
    yield put(editAllergyFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

export function* deleteAllergySaga({ payload, onDelete, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);

  try {
    const data = yield call(
      api(token).delete,
      `/api/resident-allergy/${payload?.delete_id}`,
    );
    if (data) {
      yield put(successAlert("Allergy deleted successfully!"));
      yield put(deleteAllergySuccess());
      typeof onDelete === "function" && onDelete();
    }
  } catch (e) {
    yield put(deleteAllergyFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
  }
}

// Register facade — one read model per (resident, register): rows + rag +
// assertions off the existing allergy CRUD data. Advisory read: on failure the
// facade degrades to its "not yet confirmed" state, so no error toast — but the
// failure IS recorded (registersError, keyed) so a register surface can tell a
// failed fetch apart from an empty register.
export function* fetchAllergyRegisterSaga({ payload = {}, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, registerKey = "allergies" } = payload;
  const key = `${residentId}:${registerKey}`;
  try {
    const res = yield call(
      api(token).get,
      `/api/resident/${residentId}/registers/${registerKey}`,
    );
    // api() already unwraps response.data — unwrap one more { data: {...} }
    // envelope when the controller uses the standard success wrapper.
    const data =
      res && typeof res === "object" && res.data && typeof res.data === "object"
        ? res.data
        : res;
    yield put(fetchAllergyRegisterSuccess({ key, data }));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchAllergyRegisterFailure({ key }));
  }
}

// "No known allergies" (and future) register assertions. A 409 means the
// feature is pending a held migration server-side — the component disables the
// toggle with a tooltip, so don't double up with an error toast.
export function* setRegisterAssertionSaga({
  payload = {},
  onSuccess,
  onError,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const {
    residentId,
    registerKey = "allergies",
    assertion = "none_known",
    assertionUuid = null,
    active,
  } = payload;
  try {
    let data;
    if (active) {
      data = yield call(
        api(token).post,
        `/api/resident/${residentId}/register-assertions`,
        { register_key: registerKey, assertion },
      );
    } else {
      // Removal is by assertion uuid (the API only routes
      // DELETE resident/{id}/register-assertions/{uuid}); the component reads
      // the uuid off the register read model. Without one there is nothing to
      // delete — refetch so the toggle resyncs instead of firing a 405.
      const uuid =
        assertionUuid ??
        (yield select((state) => {
          const register =
            state?.ResidentAllergyReducer?.registers?.[
              `${residentId}:${registerKey}`
            ];
          const match = (register?.assertions ?? []).find(
            (a) => (a?.assertion ?? a?.key) === assertion,
          );
          return match?.id ?? null;
        }));
      if (!uuid) {
        yield put(setRegisterAssertionFailure());
        typeof onSuccess === "function" && onSuccess(null);
        return;
      }
      data = yield call(
        api(token).delete,
        `/api/resident/${residentId}/register-assertions/${uuid}`,
      );
    }
    if (data) {
      yield put(setRegisterAssertionSuccess());
      // The register carries one status assertion: none_known | unable_to_assess.
      yield put(
        successAlert(
          active
            ? assertion === "unable_to_assess"
              ? "Recorded: allergy status could not be assessed."
              : "Confirmed: no known allergies."
            : "Register status cleared.",
        ),
      );
      typeof onSuccess === "function" && onSuccess(data);
    }
  } catch (e) {
    yield put(setRegisterAssertionFailure());
    typeof onError === "function" && onError(e);
    if (e?.response?.status !== 409) {
      if (typeof e === "string") yield put(errorAlert(e));
      else if (typeof e.error === "string") yield put(errorAlert(e.error));
    }
  }
}

export function* fetchAllergyFlow() {
  yield takeEvery(FETCH_ALLERGY, fetchAllergySaga);
}
export function* addAllergyFlow() {
  yield takeEvery(ADD_ALLERGY, addAllergySaga);
}
export function* editAllergyFlow() {
  yield takeEvery(EDIT_ALLERGY, editAllergySaga);
}
export function* deleteAllergyFlow() {
  yield takeEvery(DELETE_ALLERGY, deleteAllergySaga);
}
export function* fetchAllergyRegisterFlow() {
  yield takeEvery(FETCH_ALLERGY_REGISTER, fetchAllergyRegisterSaga);
}
export function* setRegisterAssertionFlow() {
  yield takeEvery(SET_REGISTER_ASSERTION, setRegisterAssertionSaga);
}

export default function* ResidentAllergySaga() {
  yield all([
    fetchAllergyFlow(),
    addAllergyFlow(),
    editAllergyFlow(),
    deleteAllergyFlow(),
    fetchAllergyRegisterFlow(),
    setRegisterAssertionFlow(),
  ]);
}
